import React, { useContext, useState } from 'react'
import {
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { pdf } from '@react-pdf/renderer'
import moment from 'moment'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../../App'
import { LoadingSmall } from '../../loading/loadingPage'
import KPI_SECTIONS from './sections'
import useKpiDateRange from './hooks/useKpiDateRange'
import useKpi from './hooks/useKpi'
import KPIPageLayout from './components/KPIPageLayout'
import KPITile from './components/KPITile'
import KPISectionPDF from './KPISectionPDF'
import renderKpiChartImages from './pdfCharts'

// One generic page serves every section: the server's descriptor list is the
// page definition, rendered in `group` order with a subheader per group.
const KPISection = ({ slug }) => {
  const section = KPI_SECTIONS.find((entry) => entry.slug === slug)
  const dateRange = useKpiDateRange()

  // Patient experience only: feedback records flag whether the patient was
  // conveyed in an ambulance, so results can be viewed for ambulance
  // journeys, everything else, or all feedback together
  const isPatientExperience = slug === 'patient-experience'
  const [ambulanceFilter, setAmbulanceFilter] = useState('all')
  const query =
    isPatientExperience && ambulanceFilter !== 'all'
      ? `${dateRange.query}&ambulance=${ambulanceFilter}`
      : dateRange.query

  const { data, loading, error } = useKpi(`/kpi-dashboard/section/${slug}`, query)

  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [generatingReport, setGeneratingReport] = useState(false)

  const handleDownloadReport = async () => {
    setGeneratingReport(true)
    try {
      const startLabel = moment(dateRange.value.startDate).format('Do MMMM YYYY')
      const endLabel = moment(dateRange.value.endDate).format('Do MMMM YYYY')
      const blob = await pdf(
        <KPISectionPDF
          section={{ ...data, name: section?.name ?? data.name }}
          charts={renderKpiChartImages(data.kpis)}
          orgSettings={orgSettings}
          rangeLabel={`${startLabel} – ${endLabel}`}
          printedBy={`Generated by ${user.firstName} ${user.lastName} on ${moment().format('Do MMMM YYYY, HH:mm')}`}
        />,
      ).toBlob()

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${(section?.name ?? slug).replace(/[^\w]+/g, '_')}_KPI_Report_${moment(dateRange.value.startDate).format('DDMMYYYY')}-${moment(dateRange.value.endDate).format('DDMMYYYY')}.pdf`
      link.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error(e)
      setToast({ message: 'Unable to generate the PDF report', severity: 'error' })
    } finally {
      setGeneratingReport(false)
    }
  }

  const groups = []
  ;(data?.kpis ?? []).forEach((kpi) => {
    const groupName = kpi.group ?? 'General'
    let group = groups.find((entry) => entry.name === groupName)
    if (!group) {
      group = { name: groupName, kpis: [] }
      groups.push(group)
    }
    group.kpis.push(kpi)
  })

  return (
    <KPIPageLayout
      title={section?.name ?? data?.name ?? 'KPIs'}
      subtitle={section?.blurb}
      backTo={`/kpi${dateRange.search}`}
      dateRange={dateRange}
      healthScore={loading || error ? undefined : data?.healthScore}
      actions={
        <>
          <Tooltip title="Download this section as a PDF report">
            <span>
              <IconButton
                onClick={handleDownloadReport}
                disabled={generatingReport || loading || !!error || !data}
              >
                {generatingReport ? <CircularProgress size={22} /> : <PictureAsPdfIcon />}
              </IconButton>
            </span>
          </Tooltip>
          {isPatientExperience && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={ambulanceFilter}
            onChange={(e, value) => value && setAmbulanceFilter(value)}
          >
            <ToggleButton value="all" sx={{ textTransform: 'none' }}>
              All Feedback
            </ToggleButton>
            <ToggleButton value="ambulance" sx={{ textTransform: 'none' }}>
              Ambulance
            </ToggleButton>
            <ToggleButton value="nonAmbulance" sx={{ textTransform: 'none' }}>
              Non-Ambulance
            </ToggleButton>
          </ToggleButtonGroup>
          )}
        </>
      }
    >
      {error && <Alert severity="error">Unable to load this section. Please try again.</Alert>}
      {loading ? (
        <LoadingSmall />
      ) : (
        groups.map((group) => (
          <React.Fragment key={group.name}>
            <Typography variant="title3" sx={{ mt: 1 }}>
              {group.name}
            </Typography>
            <Grid container spacing={2}>
              {group.kpis.map((kpi) => (
                <KPITile key={kpi.key} kpi={kpi} />
              ))}
            </Grid>
          </React.Fragment>
        ))
      )}
      {!loading && !error && !groups.length && (
        <Alert severity="info">No KPI data available for the selected date range.</Alert>
      )}
    </KPIPageLayout>
  )
}

export default KPISection
